import React, { useState, useEffect, useContext } from 'react'
import { RouteComponentProps, useLocation, useHistory } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { FlashMessage, LoadingCircle } from '../../../components/atoms'
import { CreatorPublishedProductsTpl, CreatorTwoColumnLayout, DialogTpl } from '../../../components/templates'
import { Meta } from '../../../Meta'
import { FlashMessageType } from '../../../types/myTypes'
import {
  PublishedProduct,
  usePagedPublishedProductsLazyQuery,
  useCreatorFinishProductMutation,
  useCreatorDisableProductMutation,
} from '../../../types/graphql'
import { useValidateCreatorToken } from '../../../lib/ValidateCreatorToken'
import { useSetCreatorProfile } from '../../../lib/SetMyProfile'
import { CreatorProfileContext } from '../../../context'

type Props = {} & RouteComponentProps

export const CreatorPublishedProducts: React.FC<Props> = (props) => {
  const [publishedProducts, setPublishedProducts] = useState<PublishedProduct[]>([])
  const [selectedProductId, setSelectedProductId] = useState<string>('')
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [lastPage, setLastPage] = useState<number>(1)
  const [flashMessage, setFlashMessage] = useState<FlashMessageType | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [dialogVisible, setDialogVisible] = useState<boolean>(false)
  const creatorId = useContext(CreatorProfileContext).creator.id

  const history = useHistory()
  const location = useLocation<{ registerProductComplete: boolean }>()

  const [pagedPublishedProductsLazyQuery] = usePagedPublishedProductsLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data) {
        setLoading(false)
        setPublishedProducts(data.PagedPublishedProducts?.data ?? [])
        setLastPage(data.PagedPublishedProducts?.paginatorInfo.lastPage ?? 1)
      }
    },
    onError: (e) => {
      setLoading(false)
      setFlashMessage({ type: 'systemError', message: e.message })
      Sentry.captureException(e)
    },
  })
  const [creatorDisableProductMutation] = useCreatorDisableProductMutation({
    onCompleted: () => {
      setFlashMessage({ type: 'success', message: '選択した商品を非表示にしました' })
      pagedPublishedProductsLazyQuery({
        variables: {
          creator_id: creatorId,
          first: 20,
          page: currentPage,
        },
      })
    },
    onError: (e) => {
      setLoading(false)
      Sentry.captureException(e)
      if (e.message) {
        setFlashMessage({ type: 'inputError', message: e.message })
      } else {
        setFlashMessage({ type: 'systemError', message: '更新できませんでした' })
      }
    },
  })

  const [creatorFinishProductMutation] = useCreatorFinishProductMutation({
    onCompleted: () => {
      pagedPublishedProductsLazyQuery({
        variables: {
          creator_id: creatorId,
          first: 20,
          page: currentPage,
        },
      })
      setFlashMessage({ type: 'success', message: '選択した商品を販売終了にしました' })
    },
    onError: (e) => {
      setLoading(false)
      Sentry.captureException(e)
      if (e.message) {
        setFlashMessage({ type: 'inputError', message: e.message })
      } else {
        setFlashMessage({ type: 'systemError', message: '更新できませんでした' })
      }
    },
  })

  const onClickToDisabled = (productId: string): void => {
    setLoading(true)
    setFlashMessage(null)
    creatorDisableProductMutation({
      variables: {
        product_id: productId,
      },
    })
  }

  const onClickToEnd = (productId: string): void => {
    setDialogVisible(true)
    setSelectedProductId(productId)
  }

  const onClickToEndDialogOk = (): void => {
    setDialogVisible(false)
    setLoading(true)
    setFlashMessage(null)
    creatorFinishProductMutation({
      variables: {
        product_id: selectedProductId,
      },
    })
  }

  const onClickToEndDialogCancel = (): void => {
    setDialogVisible(false)
  }

  useValidateCreatorToken()
  useSetCreatorProfile()
  useEffect(() => {
    if (location.state) {
      if (location.state.registerProductComplete) {
        setFlashMessage({ type: 'success', message: '登録が完了しました' })
      }
    }
  }, [location.state])

  // location.state.registerProductComplete = trueのままだとリロード時に毎回フラッシュメッセージが表示されるのでreplace
  useEffect(() => {
    if (location.state && location.state.registerProductComplete === true) {
      history.replace({ ...location, state: undefined })
    }
  }, [history, location])

  useEffect(() => {
    const pageParam = new URLSearchParams(props.location.search).get('page')
    if (pageParam) {
      setCurrentPage(Number(pageParam))
    }

    if (creatorId) {
      pagedPublishedProductsLazyQuery({
        variables: {
          creator_id: creatorId,
          first: 20,
          page: Number(pageParam),
        },
      })
    }
  }, [props.location.search, creatorId, pagedPublishedProductsLazyQuery])

  return (
    <>
      <Meta title="公開中の商品一覧" />
      {dialogVisible && (
        <DialogTpl title="この商品を販売終了にしますか？" onClickOk={onClickToEndDialogOk} onClickCancel={onClickToEndDialogCancel}>
          販売終了にすると、この商品を再度販売できなくなります。再販する場合、新たに商品登録をお願い致します。
        </DialogTpl>
      )}
      {loading && <LoadingCircle />}
      {flashMessage && <FlashMessage flashMessage={flashMessage} />}
      <CreatorTwoColumnLayout currentTab="products">
        <CreatorPublishedProductsTpl
          currentPage={currentPage}
          lastPage={lastPage}
          publishedProducts={publishedProducts}
          onClickToDisabled={onClickToDisabled}
          onClickToEnd={onClickToEnd}
        />
      </CreatorTwoColumnLayout>
    </>
  )
}
